/**
 * Escape closes the top-most overlay, everywhere.
 *
 * The product has ~75 hand-rolled dialogs across 47 files plus dozens of
 * popovers, and almost none of them listen for Escape. Rather than add a key
 * handler to each one, this does what the user would do with the mouse:
 *
 *   - an open list or menu, or a popover → a `mousedown` outside it, which is
 *     what every one of them already closes on;
 *   - a dialog → its own Cancel / Close button, or failing that its backdrop.
 *
 * So no dialog gains a new way to close, only a new key for an existing one,
 * and whatever its Cancel button does (reset a form, clear a selection) still
 * happens.
 *
 * It waits a frame before acting. A component that DOES handle Escape itself
 * has closed by then, and is left alone instead of being closed twice or
 * taking the layer beneath it down with it.
 */

const DIALOG = '[role="dialog"], [role="alertdialog"], [aria-modal="true"]';
const OPEN_LIST = '[role="listbox"], [role="menu"]';
const CLOSE_TEXT = /^(cancel|close)$/i;

function isVisible(el: Element): boolean {
  return el.getClientRects().length > 0;
}

function lastVisible(selector: string): HTMLElement | null {
  const all = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(isVisible);
  return all.length ? all[all.length - 1] : null;
}

/** A full-screen backdrop: how a dialog with no `role` is recognised. */
function topBackdrop(): HTMLElement | null {
  const all = Array.from(document.querySelectorAll<HTMLElement>('.fixed.inset-0')).filter(
    (el) => isVisible(el) && getComputedStyle(el).pointerEvents !== 'none',
  );
  return all.length ? all[all.length - 1] : null;
}

function findCloseButton(scope: HTMLElement): HTMLButtonElement | null {
  const buttons = Array.from(scope.querySelectorAll<HTMLButtonElement>('button')).filter(
    (b) => !b.disabled && isVisible(b),
  );
  return (
    buttons.find((b) => /^(close|dismiss)\b/i.test(b.getAttribute('aria-label') ?? '')) ??
    buttons.find((b) => CLOSE_TEXT.test((b.textContent ?? '').trim())) ??
    buttons.find((b) => !(b.textContent ?? '').trim() && b.querySelector('svg.lucide-x')) ??
    null
  );
}

function clickOutside(): void {
  // Both events: some popovers listen for one, some for the other.
  for (const type of ['mousedown', 'click'] as const) {
    document.body.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
  }
}

function closeDialog(dialog: HTMLElement): void {
  // A role-less dialog is found by its backdrop; one WITH a role may sit
  // inside its backdrop or beside it, so search the nearest one too.
  const backdrop = dialog.matches('.fixed.inset-0')
    ? dialog
    : dialog.closest<HTMLElement>('.fixed.inset-0');
  const close = findCloseButton(dialog) ?? (backdrop ? findCloseButton(backdrop) : null);
  if (close) {
    close.click();
    return;
  }
  // No Cancel and no X: a click on the backdrop itself, target === currentTarget.
  (backdrop ?? dialog).dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;

  // Snapshot now; act next frame, and only on what is still open.
  const openList = lastVisible(OPEN_LIST);
  const dialog = lastVisible(DIALOG) ?? topBackdrop();

  requestAnimationFrame(() => {
    if (openList) {
      if (openList.isConnected && isVisible(openList)) clickOutside();
      return;
    }
    if (dialog) {
      if (dialog.isConnected && isVisible(dialog)) closeDialog(dialog);
      return;
    }
    clickOutside();
  });
}

let installed = false;

/** Call once at startup. Safe to call again. */
export function installEscapeToClose(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('keydown', onKeyDown);
}
